/**
 * Product Gallery Lightbox
 * Handles full-screen image display functionality with intelligent preloading
 * 
 * Features:
 * - Click to open images in full-screen overlay
 * - Left/right navigation buttons
 * - Keyboard navigation (ESC to close, arrow keys to navigate)  
 * - Click outside to close
 * - Intelligent image preloading for seamless navigation
 * - Progressive loading with loading states
 * - Memory-efficient caching
 */

interface ImageCacheItem {
  url: string;
  element: HTMLImageElement;
  loaded: boolean;
  loading: boolean;
}

export class ProductGalleryLightbox {
  private gallery: HTMLElement;
  private lightboxOverlay: HTMLElement | null;
  private lightboxImage: HTMLImageElement | null;
  private closeButton: HTMLButtonElement | null;
  private prevButton: HTMLButtonElement | null;
  private nextButton: HTMLButtonElement | null;
  private clickableImages: NodeListOf<HTMLElement>;
  private currentImageIndex = 0;
  private allImages: string[] = [];
  private imageCache: Map<string, ImageCacheItem> = new Map();
  private preloadQueue: string[] = [];
  private isPreloading = false;
  private maxPreloadDistance = 3; // Preload 3 images ahead/behind current
  private loadingIndicator: HTMLElement | null = null;
  private intersectionObserver: IntersectionObserver | null = null;

  constructor(gallery: HTMLElement) {
    this.gallery = gallery;
    this.lightboxOverlay = document.querySelector('[data-lightbox-overlay]');
    this.lightboxImage = document.querySelector('[data-lightbox-image]');
    this.closeButton = document.querySelector('[data-lightbox-close]');
    this.prevButton = document.querySelector('[data-lightbox-prev]');
    this.nextButton = document.querySelector('[data-lightbox-next]');
    this.clickableImages = this.gallery.querySelectorAll('[data-lightbox-trigger]');
    // If no direct elements found, try looking for img elements with the attribute
    if (this.clickableImages.length === 0) {
      this.clickableImages = this.gallery.querySelectorAll('img[data-lightbox-trigger]');
    }
    
    this.init();
  }

  private init(): void {
    this.collectImageData();
    this.createLoadingIndicator();
    this.bindEvents();
    this.initializeImageCache();
    this.preloadFirstBatch();
  }

  private collectImageData(): void {
    // Collect all image sources for navigation (excluding video containers completely)
    const mediaContainers = this.gallery.querySelectorAll('.c-product-gallery__media-container');
    
    mediaContainers.forEach((container) => {
      // Skip the video carousel item (it's the last media shown in mobile)
      if (container.classList.contains('c-product-gallery__video-carousel-item')) {
        return;
      }
      
      // Check for video elements first - if any video element exists, skip this container entirely
      const videoElement = container.querySelector('video');
      if (videoElement) {
        return; // Skip any container with video elements
      }
      
      // Only collect from pure image containers
      const imgElement = container.querySelector('img[data-lightbox-src]') as HTMLImageElement;
      
      // Only add to lightbox if it's an image and has lightbox data
      if (imgElement) {
        const lightboxSrc = imgElement.dataset.lightboxSrc;
        if (lightboxSrc) {
          this.allImages.push(lightboxSrc);
        }
      }
    });
  }

  private createLoadingIndicator(): void {
    if (!this.lightboxOverlay) return;

    this.loadingIndicator = document.createElement('div');
    this.loadingIndicator.className = 'c-product-gallery__lightbox-loading';
    this.loadingIndicator.innerHTML = `
      <div class="c-product-gallery__lightbox-spinner">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="60" stroke-dashoffset="60">
            <animate attributeName="stroke-dashoffset" dur="2s" values="60;0;60" repeatCount="indefinite"/>
          </circle>
        </svg>
      </div>
    `;
    this.lightboxOverlay.appendChild(this.loadingIndicator);
  }

  private initializeImageCache(): void {
    // Initialize cache for all images
    this.allImages.forEach(url => {
      if (!this.imageCache.has(url)) {
        this.imageCache.set(url, {
          url,
          element: new Image(),
          loaded: false,
          loading: false
        });
      }
    });
  }

  private preloadFirstBatch(): void {
    // Preload first 3 images immediately for faster initial experience
    const initialBatch = this.allImages.slice(0, Math.min(3, this.allImages.length));
    initialBatch.forEach(url => this.preloadImage(url));
    
    // Set up intersection observer for gallery images to preload when they come into view
    this.setupIntersectionObserver();
  }

  private setupIntersectionObserver(): void {
    if (!('IntersectionObserver' in window)) return;

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const lightboxSrc = img.dataset.lightboxSrc;
          if (lightboxSrc) {
            // Preload with low priority when image comes into view
            this.preloadImage(lightboxSrc, 'low').catch(() => {});
          }
        }
      });
    }, {
      root: null,
      rootMargin: '100px', // Start loading 100px before image is visible
      threshold: 0.1
    });

    // Observe all gallery images
    const galleryImages = this.gallery.querySelectorAll('img[data-lightbox-src]');
    galleryImages.forEach(img => {
      this.intersectionObserver?.observe(img);
    });
  }

  private preloadImage(url: string, priority: 'high' | 'low' = 'low'): Promise<void> {
    return new Promise((resolve, reject) => {
      const cacheItem = this.imageCache.get(url);
      if (!cacheItem || cacheItem.loaded || cacheItem.loading) {
        resolve();
        return;
      }

      cacheItem.loading = true;
      const img = cacheItem.element;
      
      // Set loading priority for better performance
      if ('loading' in img) {
        img.loading = priority === 'high' ? 'eager' : 'lazy';
      }

      // Find corresponding srcset from DOM if available
      const galleryImages = this.gallery.querySelectorAll('img[data-lightbox-src]');
      let srcset = '';
      
      galleryImages.forEach((galleryImg) => {
        const imgElement = galleryImg as HTMLImageElement;
        if (imgElement.dataset.lightboxSrc === url && imgElement.dataset.lightboxSrcset) {
          srcset = imgElement.dataset.lightboxSrcset;
        }
      });

      img.onload = () => {
        cacheItem.loaded = true;
        cacheItem.loading = false;
        resolve();
      };

      img.onerror = () => {
        cacheItem.loading = false;
        reject(new Error(`Failed to load image: ${url}`));
      };

      // Set srcset for responsive loading if available
      if (srcset) {
        img.srcset = srcset;
        img.sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1600px';
      }
      
      img.src = url;
    });
  }

  private preloadSurroundingImages(currentIndex: number): void {
    if (this.isPreloading) return;
    this.isPreloading = true;

    const imagesToPreload = new Set<string>();
    
    // Preload images around current position
    for (let i = 1; i <= this.maxPreloadDistance; i++) {
      // Next images
      const nextIndex = (currentIndex + i) % this.allImages.length;
      imagesToPreload.add(this.allImages[nextIndex]);
      
      // Previous images
      const prevIndex = (currentIndex - i + this.allImages.length) % this.allImages.length;
      imagesToPreload.add(this.allImages[prevIndex]);
    }

    // Preload in order of likelihood to be viewed next
    const preloadPromises = Array.from(imagesToPreload).map(url => 
      this.preloadImage(url, 'low').catch(() => {}) // Ignore errors for background preloading
    );

    Promise.allSettled(preloadPromises).finally(() => {
      this.isPreloading = false;
    });
  }

  private bindEvents(): void {
    // Handle image clicks to open lightbox using event delegation
    this.gallery.addEventListener('click', (e) => {
      const clickedElement = e.target as HTMLElement;
      
      // Find the closest media container
      const mediaContainer = clickedElement.closest('.c-product-gallery__media-container');
      if (mediaContainer) {
        // Skip the video carousel item (it's not in lightbox)
        if (mediaContainer.classList.contains('c-product-gallery__video-carousel-item')) {
          return;
        }
        
        // Check for video elements first - if any video element exists, skip this container entirely
        const videoElement = mediaContainer.querySelector('video');
        if (videoElement) {
          return; // Skip any container with video elements - don't open lightbox
        }
        
        // Find the img element within the container
        const imgElement = mediaContainer.querySelector('img[data-lightbox-trigger]');
        
        // Only handle clicks on pure image containers
        if (imgElement) {
          // Calculate the correct index based on position in IMAGE containers only (no videos)
          const imageContainers = Array.from(
            this.gallery.querySelectorAll('.c-product-gallery__media-container:not(.c-product-gallery__video-carousel-item)')
          ).filter(container => {
            const hasVideo = container.querySelector('video');
            const hasImg = container.querySelector('img[data-lightbox-src]');
            return !hasVideo && hasImg; // Only pure image containers, no videos
          });
          
          const currentIndex = imageContainers.indexOf(mediaContainer);
          
          if (currentIndex >= 0) {
            e.preventDefault();
            this.openLightbox(currentIndex);
          }
        }
      }
    });

    // Handle close button
    this.closeButton?.addEventListener('click', () => {
      this.closeLightbox();
    });

    // Handle navigation button clicks
    this.prevButton?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.showPreviousImage();
    });
    
    this.nextButton?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.showNextImage();
    });

    // Handle overlay click to close (including clicking on the dark area)
    this.lightboxOverlay?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      
      // Check if the click was on the image itself, navigation buttons, or close button - if so, don't close
      const isClickOnImage = target.tagName === 'IMG' || 
                            target.closest('.c-product-gallery__lightbox-image') ||
                            target.closest('.c-product-gallery__lightbox-nav') ||
                            target.closest('.c-product-gallery__lightbox-close');
      
      // Only close if clicking outside the interactive elements
      if (!isClickOnImage) {
        this.closeLightbox();
      }
    });

    // Handle keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.isLightboxOpen()) return;

      switch (e.key) {
        case 'Escape':
          this.closeLightbox();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          this.showPreviousImage();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.showNextImage();
          break;
      }
    });
  }

  private openLightbox(imageIndex: number): void {
    if (!this.lightboxOverlay || !this.lightboxImage) return;

    this.currentImageIndex = imageIndex;
    
    // Show the lightbox first
    this.lightboxOverlay.classList.add('is-active');
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
    
    // Then load the image
    this.showImage(imageIndex);
  }

  private closeLightbox(): void {
    if (!this.lightboxOverlay) return;

    this.lightboxOverlay.classList.remove('is-active');
    document.body.style.overflow = ''; // Restore background scrolling
  }

  private showImage(index: number): void {
    if (!this.lightboxImage || index < 0 || index >= this.allImages.length) return;

    this.currentImageIndex = index;
    const imageUrl = this.allImages[index];
    const cacheItem = this.imageCache.get(imageUrl);

    if (!cacheItem) return;

    if (cacheItem.loaded) {
      // Image is already cached - instant display without loading indicator
      this.displayImageWithSync(cacheItem.element.src, index);
    } else {
      // Check if image might be in browser cache first
      const testImg = new Image();
      testImg.src = imageUrl;
      
      if (testImg.complete && testImg.naturalWidth > 0) {
        // Image is in browser cache - display immediately
        this.displayImageWithSync(imageUrl, index);
      } else {
        // Show loading indicator ON TOP of current image while new image loads
        this.showLoadingIndicator();
        
        // Preload with high priority for immediate viewing
        this.preloadImage(imageUrl, 'high').then(() => {
          // Image is ready - displayImageWithSync will hide loading and switch image
          this.displayImageWithSync(imageUrl, index);
        }).catch(() => {
          // Fallback: load image directly with sync
          this.displayImageDirectWithSync(imageUrl, index);
        });
      }
    }

    // Trigger preloading of surrounding images for smoother navigation
    this.preloadSurroundingImages(index);
  }

  private displayImage(src: string, index: number): void {
    if (!this.lightboxImage) return;
    
    // Add fade transition for smoother experience
    this.lightboxImage.style.opacity = '0';
    
    setTimeout(() => {
      this.lightboxImage!.src = src;
      this.lightboxImage!.alt = `Product image ${index + 1}`;
      this.lightboxImage!.style.opacity = '1';
    }, 50);
  }

  private displayImageWithSync(src: string, index: number): void {
    if (!this.lightboxImage) return;
    
    // Keep current image visible while loading
    // Don't fade out until new image is ready
    
    // Create a new image to test if it's actually ready
    const testImg = new Image();
    
    testImg.onload = () => {
      // Image is fully loaded and ready to display
      if (this.lightboxImage) {
        // Quick fade transition to new image
        this.lightboxImage.style.opacity = '0';
        
        setTimeout(() => {
          if (this.lightboxImage) {
            this.lightboxImage.src = src;
            this.lightboxImage.alt = `Product image ${index + 1}`;
            
            // Hide loading indicator and show new image simultaneously
            this.hideLoadingIndicator();
            this.lightboxImage.style.opacity = '1';
          }
        }, 100); // Slightly longer for smooth transition
      }
    };

    testImg.onerror = () => {
      // If image fails to load, hide loading but keep current image
      this.hideLoadingIndicator();
    };

    // Test if the image is actually ready
    testImg.src = src;
    
    // If image is already in browser cache, onload fires immediately
    if (testImg.complete && testImg.naturalWidth > 0) {
      testImg.onload = null;
      if (this.lightboxImage) {
        // Instant switch for cached images
        this.lightboxImage.style.opacity = '0';
        
        setTimeout(() => {
          if (this.lightboxImage) {
            this.lightboxImage.src = src;
            this.lightboxImage.alt = `Product image ${index + 1}`;
            this.hideLoadingIndicator();
            this.lightboxImage.style.opacity = '1';
          }
        }, 50);
      }
    }
  }

  private displayImageDirectWithSync(src: string, index: number): void {
    if (!this.lightboxImage) return;
    
    // Keep current image visible while loading
    // Don't fade out until new image is ready to display
    
    // Set up load event listener for the lightbox image itself
    const handleLoad = () => {
      // New image has loaded - now we can hide loading and show new image
      this.hideLoadingIndicator();
      if (this.lightboxImage) {
        this.lightboxImage.style.opacity = '1';
        this.lightboxImage.removeEventListener('load', handleLoad);
        this.lightboxImage.removeEventListener('error', handleError);
      }
    };

    const handleError = () => {
      // Hide loading but keep current image visible
      this.hideLoadingIndicator();
      if (this.lightboxImage) {
        this.lightboxImage.removeEventListener('load', handleLoad);
        this.lightboxImage.removeEventListener('error', handleError);
      }
    };

    this.lightboxImage.addEventListener('load', handleLoad);
    this.lightboxImage.addEventListener('error', handleError);

    // Fade out current image just before setting new source
    setTimeout(() => {
      if (this.lightboxImage) {
        this.lightboxImage.style.opacity = '0';
        
        // Set new source after fade starts
        setTimeout(() => {
          if (this.lightboxImage) {
            this.lightboxImage.src = src;
            this.lightboxImage.alt = `Product image ${index + 1}`;
          }
        }, 100);
      }
    }, 50);
  }

  private showLoadingIndicator(): void {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'flex';
      this.loadingIndicator.classList.add('is-visible');
    }
  }

  private hideLoadingIndicator(): void {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.remove('is-visible');
      // Small delay to allow fade transition before hiding completely
      setTimeout(() => {
        if (this.loadingIndicator) {
          this.loadingIndicator.style.display = 'none';
        }
      }, 100);
    }
  }

  private showNextImage(): void {
    const nextIndex = (this.currentImageIndex + 1) % this.allImages.length;
    this.showImage(nextIndex);
    
    // Periodic cleanup to manage memory usage
    if (nextIndex % 5 === 0) {
      this.cleanupImageCache();
    }
  }

  private showPreviousImage(): void {
    const prevIndex = (this.currentImageIndex - 1 + this.allImages.length) % this.allImages.length;
    this.showImage(prevIndex);
    
    // Periodic cleanup to manage memory usage  
    if (prevIndex % 5 === 0) {
      this.cleanupImageCache();
    }
  }

  private isLightboxOpen(): boolean {
    return this.lightboxOverlay?.classList.contains('is-active') || false;
  }

  // Public method to get current image index
  public getCurrentImageIndex(): number {
    return this.currentImageIndex;
  }

  // Public method to open lightbox with specific image
  public openWithImage(index: number): void {
    this.openLightbox(index);
  }

  // Memory management - clear unused cached images
  private cleanupImageCache(): void {
    const currentIndex = this.currentImageIndex;
    const keepDistance = this.maxPreloadDistance + 1;
    
    this.imageCache.forEach((cacheItem, url) => {
      const imageIndex = this.allImages.indexOf(url);
      const distance = Math.min(
        Math.abs(imageIndex - currentIndex),
        Math.abs(imageIndex - currentIndex + this.allImages.length),
        Math.abs(imageIndex - currentIndex - this.allImages.length)
      );
      
      // Remove images that are far from current position
      if (distance > keepDistance && cacheItem.loaded) {
        cacheItem.element.src = '';
        cacheItem.loaded = false;
      }
    });
  }

  // Public method to destroy the instance
  public destroy(): void {
    // Disconnect intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    
    // Clear image cache
    this.imageCache.forEach(cacheItem => {
      cacheItem.element.src = '';
    });
    this.imageCache.clear();
    
    // Remove loading indicator
    if (this.loadingIndicator && this.lightboxOverlay) {
      this.lightboxOverlay.removeChild(this.loadingIndicator);
    }

    // Remove all event listeners
    // Note: Event delegation listeners are handled by removing from gallery element
    // Individual method bindings need to be tracked properly

    // Restore body overflow in case lightbox was open
    document.body.style.overflow = '';
  }
}

// Note: ProductGalleryLightbox is initialized by the ProductMain section class
// No auto-initialization needed here
